import { useContext, useEffect, useRef, useState } from 'react';
import { useForm, useFormState, Controller } from 'react-hook-form';

import { Message } from '@components';
import { pageTypeLookup, pageTypeTooltipLookup } from '@constants';
import get from 'lodash/get';
import {
  FormFieldset,
  PathField,
  SelectField,
  TextField,
  TextAreaField,
  FieldWrapper,
  ToggleField,
  DatamodelSubTypeField,
  LocationsField,
} from '@Forms/fields.jsx';
import {
  addSlash,
  pathFormat,
  slugify,
  removeNewlines,
  slugifyUnderscore,
  slugifyOnBlur,
  urlFormat,
} from '@Forms/formatters.js';
import { errorsFromMutate } from '@Forms/utils.js';
import { forceTrailingSlash } from '@helpers';
import { ButtonSpinnerIcon, ButtonSpinnerDarkIcon } from '@svgs';
import { GlobalContext } from '@contexts';
const pageAttributesByType = (pageType) => {
  // return the standard Page attributes based on the type
  //
  // used to pre-populate modules on the page
  switch (pageType) {
    case 'blog':
      return {
        column_widths: [[100]],
        modules: [
          {
            module: 'blog',
            column: 1,
            order: 1,
            row: 1,
          },
        ],
        page_options: {
          post_detail_url_pattern: '{post_slug}',
        },
      };
    case 'directory':
      return {
        column_widths: [[100]],
        modules: [
          {
            module: 'directory',
            column: 1,
            order: 1,
            row: 1,
          },
        ],
      };
    case 'sitesearch':
      return {
        column_widths: [[100]],
        noindex: true,
        modules: [
          {
            module: 'sitesearch',
            column: 1,
            order: 1,
            row: 1,
          },
        ],
      };
  }
  return {};
};

export const AddPageForm = ({
  disabled,
  mode = 'content',
  mutation,
  onCancel,
  pageTemplates,
  siteData,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
    setError,
    setFocus,
    watch,
  } = useForm({
    defaultValues: {
      active: true,
    },
    mode: 'onBlur',
  });
  const { isDirty, isValid } = useFormState({ control });

  // state used based on the currently selected page type
  const [typeToAdd, setTypeToAdd] = useState(false);
  // state used based to store the current custom page type if set
  const [customPageType, setCustomPageType] = useState(null);

  const globals = useContext(GlobalContext);

  const restrictPageTemplates = globals.group_settings?.restrict_page_templates;

  useEffect(() => {
    // set focus to the name field if this is a new project
    setFocus('name');
  }, [setFocus]);

  const onSubmit = async (data) => {
    const payload = {
      ...pageAttributesByType(typeToAdd),
      ...data,
    };
    // set site_id
    payload.site_id = siteData.id;
    // need to force a slash in case path field was not blurred
    payload.path = forceTrailingSlash(data.path);
    // type from state
    payload.type = typeToAdd;
    // custom page type for templated pages
    payload.custom_page_type =
      typeToAdd == 'template_page' ? customPageType : null;

    mutation.mutate(payload, {
      onError: (error) => {
        // handle server-side validations and errors
        const errorsList = errorsFromMutate(error);
        // Only set field errors, skip __all__ errors as they are handled in the parent component
        errorsList
          .filter(({ field }) => field !== '__all__')
          .forEach(({ field, error }) => setError(field, error));
      },
    });
  };

  // change event on page type toggle
  const typeToAddChange = (e) => {
    // split the value as it can have the custom page type in it
    const splittedValues = e.target.value.split(':');
    setTypeToAdd(splittedValues[0]);
    setCustomPageType(splittedValues.length > 1 ? splittedValues[1] : null);
  };

  // combined isPending and isError
  const isError = mutation.isError;
  const isPending = mutation.isPending;

  const isRestricted = restrictPageTemplates?.length > 0;

  const filteredPageTemplates = isRestricted
    ? pageTemplates.filter((p) =>
        restrictPageTemplates.includes(p.template_name),
      )
    : pageTemplates;

  console.log('restrictPageTemplates', restrictPageTemplates);
  console.log('isRestricted', isRestricted);
  console.log('filteredPageTemplates', filteredPageTemplates);
  console.log('pageTemplates', pageTemplates);

  // related datamodel choices for dropdown
  const templateChoices = filteredPageTemplates.map((p) => {
    return (
      <option key={p.id} value={`template_page:${p.template_name}`}>
        {p.name}
        {mode == 'template' && <> (Templated page)</>}
      </option>
    );
  });

  let pathPrefix = '';
  if (siteData.type == 'template') {
    pathPrefix = '/location-path';
  } else if (siteData.adjusted_base_directory != '/') {
    // normal base directory site
    pathPrefix = siteData.adjusted_base_directory.slice(0, -1);
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={
        `sb-manage-content-form ` + (disabled ? 'sb-form-disabled' : '')
      }
    >
      <FormFieldset>
        <TextField
          register={register}
          error={errors.name}
          options={{ required: 'This field is required' }}
          name={`name`}
          label="Page name"
          helpText="Name of the page used within navigations and menus"
          placeholder="My page"
          autoComplete="off"
        />
        <SelectField
          register={register}
          error={errors.type}
          helpText={
            typeToAdd
              ? `${pageTypeLookup[typeToAdd]} - ${pageTypeTooltipLookup[typeToAdd]}`
              : ''
          }
          options={{
            required: 'This field is required',
            onChange: typeToAddChange,
          }}
          name="type"
          label="Type of page"
        >
          <option value="">Choose a type...</option>
          {!isRestricted && <option value="default">Blank normal page</option>}
          {templateChoices}
          {mode == 'template' && (
            <>
              <option value="blog">{pageTypeLookup['blog']}</option>
              <option value="directory">{pageTypeLookup['directory']}</option>
              <option value="sitesearch">{pageTypeLookup['sitesearch']}</option>
              <option value="subpath">{pageTypeLookup['subpath']}</option>
              <option value="template">{pageTypeLookup['template']}</option>
              <option value="custom404">{pageTypeLookup['custom404']}</option>
            </>
          )}
        </SelectField>
      </FormFieldset>
      {typeToAdd && (
        <FormFieldset legend="Publishing options">
          <ToggleField
            register={register}
            control={control}
            error={errors.active}
            name="active"
            label="Published"
            helpText="When unpublished, will 404 if visited."
          />
          <TextField
            register={register}
            error={errors.custom_field_published}
            name={`custom_field_published`}
            label="Published status from custom field"
            options={{
              onChange: slugifyUnderscore,
              onBlur: (e) => slugifyOnBlur(e, '_'),
            }}
            helpText="Key reference to the Site custom field that overrides the Page published status (i.e. some_field)"
          />
          {siteData.business_id && (
            <FieldWrapper
              error={errors.locations}
              label="Assign locations"
              name={'locations'}
            >
              <Controller
                control={control}
                name={'locations'}
                render={({ field, value, name, ref }) => (
                  <LocationsField
                    businessID={siteData.business_id}
                    field={field}
                    defaultLocations={[]}
                  />
                )}
              />
            </FieldWrapper>
          )}
          <FieldWrapper
            error={get(errors, 'tags')}
            label="Page tag"
            name="tags"
            helpText="Special content database item type."
          >
            <Controller
              control={control}
              name="tags"
              render={({ field }) => (
                <DatamodelSubTypeField field={field} defaultOptions={[]} />
              )}
            />
          </FieldWrapper>
        </FormFieldset>
      )}
      {typeToAdd && (
        <>
          <FormFieldset legend="SEO settings">
            <PathField
              register={register}
              error={errors.path}
              options={{ onBlur: addSlash, onChange: pathFormat }}
              name="path"
              label="Page path"
              placeholder={
                watch('name')
                  ? '/' + slugify(watch('name')) + '/'
                  : '/your-page/'
              }
              helpText="If left blank, Path will be auto-assigned using the Page name"
              pathPrefix={pathPrefix}
              autoComplete="off"
            />
            <TextField
              register={register}
              error={errors.title}
              name={`title`}
              label="Page title tag"
              helpText="Set a specific title that will be used within the page title tag"
            />
            <TextAreaField
              register={register}
              error={errors.meta_description}
              options={{ onChange: removeNewlines }}
              name="meta_description"
              label="Meta description"
              rows={3}
              helpText="Page meta description for the page."
            />
            <TextAreaField
              register={register}
              error={errors.meta_keywords}
              options={{ onChange: removeNewlines }}
              name="meta_keywords"
              label="Meta keywords"
              rows={2}
              helpText="Page meta keywords for the page"
            />
            <ToggleField
              register={register}
              control={control}
              error={errors.noindex}
              name="noindex"
              label="noindex"
              helpText="Set Page to Robots noindex for search engines."
            />
            <ToggleField
              register={register}
              control={control}
              error={errors.nofollow}
              name="nofollow"
              label="nofollow"
              helpText="Set Page to Robots nofollow for search engines."
            />
            <TextField
              register={register}
              error={errors.canonical}
              options={{ onChange: urlFormat }}
              name="canonical"
              label="Canonical URL"
              helpText="By default a canonical is set using the current page path. This field can be used to override it to point to different internal or external URL."
            />
            <TextField
              register={register}
              error={errors.custom_field_disable_canonical}
              name={`custom_field_disable_canonical`}
              label="Disable canonical from custom field"
              options={{
                onChange: slugifyUnderscore,
                onBlur: (e) => slugifyOnBlur(e, '_'),
              }}
              helpText="Key reference to the Site custom field that disables the Page canonical value (i.e. some_field)"
            />
          </FormFieldset>
        </>
      )}

      <div className="sb-manage-content-button-bar">
        <div className="sb-flex">
          <button
            className="sb-btn-cancel sb-btn"
            onClick={onCancel}
            disabled={disabled}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="sb-btn sb-btn-save"
            disabled={disabled}
          >
            {isPending ? 'Creating...' : 'Create new'}
            {isPending && <ButtonSpinnerIcon />}
          </button>
        </div>
        {!isValid && isDirty && (
          <Message messageStyle="error" title="Please review your form" />
        )}
        {isError && (
          <Message
            messageStyle="error"
            title="Error saving changes"
            message="Please review your form"
          />
        )}
      </div>
    </form>
  );
};
